import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { trpc, useTRPC } from "../../../shared/api/trpc";

export type WorkObjectListView = Awaited<ReturnType<typeof trpc.workObjects.list.query>>;
export type WorkObjectListItem = WorkObjectListView["workObjects"][number];

export type WorkObjectFieldValueFilterInput = {
  fieldDefinitionId: string;
  value: string;
};

export type WorkObjectListFilters = {
  fieldValues: WorkObjectFieldValueFilterInput[];
  typeId?: string;
};

type WorkObjectSystemInput = {
  title: string;
  typeId: string;
};

export type CreateWorkObjectInput = WorkObjectSystemInput & {
  values: Array<{
    fieldDefinitionId: string;
    value: string | string[];
  }>;
};

export type UpdateWorkObjectInput = WorkObjectSystemInput & {
  id: string;
};

export type UpdateWorkObjectDetailsInput = UpdateWorkObjectInput & {
  values: Array<{
    fieldDefinitionId: string;
    value: string | string[];
  }>;
};

export type UpdateWorkObjectFieldValuesInput = {
  workObjectId: string;
  values: Array<{
    fieldDefinitionId: string;
    value: string | string[];
  }>;
};

export function useWorkObjects() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<WorkObjectListFilters>({ fieldValues: [] });
  const listInput = useMemo(() => filters, [filters]);
  const objectsQuery = useQuery(trpc.workObjects.list.queryOptions(listInput));
  const createMutation = useMutation(trpc.workObjects.create.mutationOptions());
  const archiveMutation = useMutation(trpc.workObjects.archive.mutationOptions());
  const updateDetailsMutation = useMutation(trpc.workObjects.updateDetails.mutationOptions());
  const updateFieldValuesMutation = useMutation(trpc.workObjects.updateFieldValues.mutationOptions());

  const reload = useCallback(async (nextFilters = filters) => {
    setError(null);
    try {
      await queryClient.invalidateQueries({ queryKey: trpc.workObjects.list.queryKey(nextFilters) });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load items.");
    }
  }, [filters, queryClient, trpc]);

  async function createWorkObject(input: CreateWorkObjectInput) {
    setError(null);
    try {
      const created = await createMutation.mutateAsync(input);
      await objectsQuery.refetch();
      return created;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create item.");
      throw err;
    }
  }

  async function archiveWorkObject(id: string) {
    setError(null);
    try {
      await archiveMutation.mutateAsync({ id });
      await objectsQuery.refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not archive item.");
      throw err;
    }
  }

  async function updateWorkObjectDetails(input: UpdateWorkObjectDetailsInput) {
    setError(null);
    try {
      const updated = await updateDetailsMutation.mutateAsync(input);
      await objectsQuery.refetch();
      return updated;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update item.");
      throw err;
    }
  }

  async function updateWorkObjectFieldValues(input: UpdateWorkObjectFieldValuesInput) {
    setError(null);
    try {
      return await updateFieldValuesMutation.mutateAsync(input);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update item fields.");
      throw err;
    }
  }

  return {
    archiveWorkObject,
    createWorkObject,
    data: objectsQuery.data ?? null,
    error: error ?? (objectsQuery.error instanceof Error ? objectsQuery.error.message : objectsQuery.error ? String(objectsQuery.error) : null),
    filters,
    isArchiving: archiveMutation.isPending,
    isCreating: createMutation.isPending,
    isLoading: objectsQuery.isPending,
    isUpdating: updateDetailsMutation.isPending || updateFieldValuesMutation.isPending,
    reload,
    setFilters,
    updateWorkObjectFieldValues,
    updateWorkObjectDetails,
  };
}
